import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Icon } from './Icon';
import type { IconName } from './Icon';

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'custom';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  icon?: IconName;
  /** A leading dot in the badge's own colour, for dense lists. */
  dot?: boolean;
  size?: 'sm' | 'md';
  /**
   * With `tone="custom"`, the token colours to paint with. Always supply all
   * three so the badge is legible in both themes.
   */
  colours?: { fill: string; ink: string; edge?: string };
  children?: ReactNode;
}

const TONES: Record<Exclude<BadgeTone, 'custom'>, string> = {
  neutral: 'bg-sunken text-muted border-line',
  accent: 'bg-accent-soft text-accent-ink border-accent/25',
  ok: 'bg-ok-soft text-ok-ink border-ok/25',
  warn: 'bg-warn-soft text-warn-ink border-warn/25',
  danger: 'bg-danger-soft text-danger-ink border-danger/30',
};

/**
 * A small status label. Always carries words - a badge that is only a colour
 * is unreadable to a fair share of the people who will open this.
 */
export function Badge({
  tone = 'neutral',
  icon,
  dot = false,
  size = 'md',
  colours,
  className,
  style,
  children,
  ...rest
}: BadgeProps) {
  const custom =
    tone === 'custom' && colours
      ? ({
          ...style,
          backgroundColor: colours.fill,
          color: colours.ink,
          borderColor: colours.edge ?? 'transparent',
        } as CSSProperties)
      : style;

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border font-medium',
        size === 'sm' ? 'h-5 px-2 text-nano' : 'h-6 px-2.5 text-micro',
        tone !== 'custom' && TONES[tone],
        className,
      )}
      style={custom}
      {...rest}
    >
      {dot ? <span className="size-1.5 rounded-full bg-current" aria-hidden="true" /> : null}
      {icon ? <Icon name={icon} size={size === 'sm' ? 11 : 13} /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}
